import Component from '@bythepixel/component-loader';
import { notify } from '@Core/components/notificationBanner/utility';
import Checkbox from './components/checkbox';
import Storage from './utility/storage';
import TooltipManager from './utility/tooltipManager';
import Api from './utility/api';
import Vehicle from './components/vehicle';
import PreviewPanel from './components/previewPanel';
import { groupTitleMapping, tooltipOptions } from './config';
import Events from './events';
import EVENT_NAMES from './eventNames';

export default class VehicleComparisonController extends Component {
  static selector = '[data-comp-vehicle-comparison-controller]';

  MAX_NUMBER_OF_VEHICLES = 8;

  selectedVehicles = new Map();
  checkboxes = [];

  vehiclesList;
  previewPanel;
  groupSlug;
  groupTitle;

  constructor(containerEl) {
    super(containerEl);
    this.previewPanel = new PreviewPanel();
    this.tooltipManager = new TooltipManager(
      tooltipOptions.targetSelector,
      tooltipOptions.templateId,
    );
    this.groupSlug = containerEl.dataset.groupSlug;
    this.groupTitle = groupTitleMapping[this.groupSlug];

    this.mount();
  }

  addVehicle(vehicle) {
    if (!this.previewPanel.isActive()) {
      window.dispatchEvent(Events.Active);
      this.previewPanel.maximize();
      this.previewPanel.show();
    }

    Storage.add(this.groupSlug, vehicle);
    this.selectedVehicles.set(vehicle.stockId, vehicle);
    this.previewPanel.addVehicle(vehicle);
  }

  removeVehicle(stockId) {
    Storage.remove(this.groupSlug, stockId);
    this.selectedVehicles.delete(stockId);
    this.previewPanel.removeVehicle(stockId);
  }

  vehicleLimitReached() {
    return this.selectedVehicles.size === this.MAX_NUMBER_OF_VEHICLES;
  }

  disableSelection() {
    const checkboxesToDisable = this.checkboxes.filter(
      (checkbox) => !checkbox.selected,
    );

    checkboxesToDisable.forEach((checkbox) => checkbox.disable());
    this.tooltipManager.reload();
  }

  enableSelection() {
    this.checkboxes.forEach((checkbox) => checkbox.enable());
    this.tooltipManager.reload();
  }

  updatePreviewPanelNavigation() {
    const numberOfProductsSelected = Storage.selectedProductCount(
      this.groupSlug,
    );
    const groupTitle = numberOfProductsSelected > 1
      ? this.groupTitle
      : this.groupTitle.replace('s', '');

    const text = `Compare ${numberOfProductsSelected} ${groupTitle}`;
    const url = this.navigationUrl();

    this.previewPanel.setTitle(text);
    this.previewPanel.setNavigationText(text);
    this.previewPanel.setNavigationUrl(url);
  }

  navigationUrl() {
    const builder = new URL(window.location.origin);
    builder.pathname = `${this.groupSlug}/comparison/`;

    const formattedStockIds = Storage.getGroupsStockIds(this.groupSlug)
      .map((stockId) => `"${stockId}"`)
      .join(',');

    return `${builder.toString()}?stock-ids=[${formattedStockIds}]`;
  }

  setInitialState() {
    if (!Storage.groupHasVehicles(this.groupSlug)) {
      this.showCheckboxes();

      return;
    }

    const groupVehicles = Storage.getGroupsVehicles(this.groupSlug);
    const stockIds = Object.keys(groupVehicles);

    const checkboxesToActivate = this.checkboxes.filter((checkbox) => stockIds.includes(checkbox.stockId));

    checkboxesToActivate.forEach((checkbox) => checkbox.select(false));

    Object.values(groupVehicles).forEach((vehicleData) => {
      if (vehicleData.expired) return;

      const { stockId, cardHtml } = vehicleData;
      const vehicle = new Vehicle(stockId, cardHtml);

      this.addVehicle(vehicle);
    });

    if (this.vehicleLimitReached()) {
      this.disableSelection();
      this.tooltipManager.launch();
    }

    this.updatePreviewPanelNavigation();

    this.showCheckboxes();

    if (stockIds) {
      this.previewPanel.show();
    }
  }

  showCheckboxes() {
    this.checkboxes.forEach((checkbox) => checkbox.show());
  }

  hideCheckboxes() {
    this.checkboxes.forEach((checkbox) => checkbox.hide());
  }

  attachListeners() {
    window.addEventListener(EVENT_NAMES.VEHICLE_ADDED, async (e) => {
      const stockId = e.detail;
      let response;

      try {
        response = await Api.getProductData([stockId]);
      } catch (err) {
        console.error(err);
        notify('An unexpected error occurred while updating your comparisons.');

        this.checkboxes
          .find((checkbox) => checkbox.stockId === stockId)
          .deselect(false);

        return;
      }

      const vehicleData = response[stockId];
      const vehicle = new Vehicle(stockId, vehicleData.cardHtml);
      this.addVehicle(vehicle);
      this.updatePreviewPanelNavigation();

      if (this.vehicleLimitReached()) {
        this.disableSelection();
        this.tooltipManager.reload();
      }
    });

    window.addEventListener(EVENT_NAMES.VEHICLE_REMOVED, (e) => {
      if (this.vehicleLimitReached()) {
        this.enableSelection();
      }

      const stockId = e.detail;
      const checkbox = this.checkboxes.find(
        (_checkbox) => _checkbox.stockId === stockId,
      );
      checkbox?.deselect(false);
      this.removeVehicle(stockId);
      this.updatePreviewPanelNavigation();

      const noVehiclesSelected = this.selectedVehicles.size === 0;

      if (noVehiclesSelected) {
        this.previewPanel.reset();
        window.dispatchEvent(Events.Inactive);
      }
    });

    this.previewPanel.on(EVENT_NAMES.CLEAR_ALL, () => {
      this.activeCheckboxes().forEach((checkbox) => checkbox.deselect(true));

      Storage.clearGroup(this.groupSlug);
      this.previewPanel.reset();
      this.tooltipManager.reload();
      this.selectedVehicles.clear();
      window.dispatchEvent(Events.Inactive);
    });

    window.addEventListener('faceted-search:rendered', () => {
      this.reload();
    });
  }

  reload() {
    setTimeout(() => {
      this.initCheckboxes();
      this.tooltipManager.reload();

      if (this.vehicleLimitReached()) {
        this.disableSelection();
      }
    }, 200);
  }

  initCheckboxes() {
    const checkboxEls = document.querySelectorAll(Checkbox.selector);

    this.checkboxes = [...checkboxEls].map((checkboxEl) => {
      const checkbox = new Checkbox(checkboxEl);
      checkbox.mount();
      checkbox.show();

      if (this.selectedVehicles.has(checkbox.stockId)) {
        checkbox.select(false);
      }

      return checkbox;
    });
  }

  activeCheckboxes() {
    return this.checkboxes.filter((checkbox) => checkbox.selected);
  }

  async mount() {
    this.attachListeners();

    try {
      const syncResults = await Storage.syncData(this.groupSlug);
      this.initCheckboxes();
      this.setInitialState();
      window.dispatchEvent(Events.Ready);
    } catch (err) {
      console.error(err);
    }
  }
}
